import { ValidationError } from 'yup';
import { useMutation } from '@tanstack/react-query';
import { Items } from '@prisma/client';
import useUser from '../global/useUser';
import { itemForm, ItemForm } from '../../types/items';
import { createItem } from '../../services/items';

interface Options {
  onSuccess?: (item: Items) => void | Promise<void>;
  onError?: (message: string) => void | Promise<void>;
  onMutate?: () => void | Promise<void>;
  onSettled?: () => void | Promise<void>;
}

function useCreateItem({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { user } = useUser();

  return useMutation({
    mutationFn: async ({ payload }: { payload: ItemForm }) => {
      if (!user?.business?.id) {
        throw new Error('No business ID found');
      }

      const validatedPayload = await itemForm.noUnknown().validate(payload, {
        stripUnknown: true,
      });

      return createItem({
        businessId: user.business.id,
        payload: validatedPayload,
      });
    },
    onSuccess,
    onError: error => {
      if (error instanceof ValidationError) {
        onError?.(error.message);
        return;
      }
      onError?.('Failed to create item');
    },
    onMutate,
    onSettled,
  });
}

export default useCreateItem;
